/**
 * The severity chip used by every notification surface — the dashboard's
 * "Needs attention" card and the navbar bell.
 *
 * Shared because the two had drifted: the bell coloured `info` blue while the
 * dashboard used grey, and only the dashboard set dark text on the yellow
 * warning badge (white-on-yellow fails contrast). One component means one
 * answer to both.
 *
 * The chips are a column, not inline decoration: a fixed width keeps the
 * titles beside them aligned down the list, which is what makes a list of
 * mixed severities scannable. Without it "INFO" and "WARNING" indent their
 * neighbouring text differently on every row.
 */
import { Badge } from "react-bootstrap";

export type Severity = "error" | "warning" | "info";

// Text colour is deliberately not set here: theme.scss already forces dark
// text on .badge.bg-info / .bg-warning, because white on Bootstrap's cyan and
// yellow fails contrast badly (1.96:1 and 1.51:1). Repeating it per-component
// would give the same rule two homes that could disagree.
const VARIANT: Record<Severity, string> = {
  error: "danger",
  warning: "warning",
  info: "info",
};

export function SeverityBadge({ severity }: { severity: string }) {
  const key: Severity = (["error", "warning", "info"] as const).includes(
    severity as Severity,
  )
    ? (severity as Severity)
    : "info";
  return (
    <Badge
      bg={VARIANT[key]}
      // align-self-start: as a flex child the badge would otherwise stretch to
      // the height of a two- or three-line message beside it, turning the chip
      // into a tall bar. flex-shrink-0 stops a long title squeezing it.
      className="naas-severity-badge align-self-start flex-shrink-0 text-uppercase"
    >
      {key}
    </Badge>
  );
}
